import LiveDot from "@/components/LiveDot";
import { useLang } from "@/lib/i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getLatestFullText, stripInvisible } from "@/lib/articleText";

/**
 * Breaking-news ticker — three latest headlines, right-to-left.
 *
 * Design:
 *   - The label «عاجل» stays fixed in the black block on the reading side
 *     and links to `/section/breaking`.
 *   - The rail scrolls the three latest published articles. For each one we
 *     render the *full* title followed by the *full* body text. If the body
 *     is the same as the title (or empty) we render the title once. We
 *     never clamp lines or add ellipses.
 *   - Two identical groups of the three articles are laid out inside one
 *     flex track. Animating from `translate3d(0,0,0)` to
 *     `translate3d(-50%,0,0)` shifts by exactly one group's width → perfect
 *     seamless loop with no pixel math in the keyframes. The second group
 *     is `aria-hidden` so screen readers hear each article once.
 *   - JS only measures the actual visible group to derive the animation
 *     duration (`groupWidth / speed`, min 8 s). It never adds an off-screen
 *     probe, so the ticker cannot leak into `document.scrollWidth`.
 *   - `prefers-reduced-motion` slows the animation ×3, never stops it.
 */

const SPEED_MOBILE = 50; // px per second
const SPEED_DESKTOP = 60;
const FALLBACK_GROUP_WIDTH = 900;

// Turn `getLatestFullText` (which uses "\n\n" between title and body) into
// a single-line marquee string — a bullet separates title and body when both
// are present, and invisible unicode is stripped.
function tickerTextFor(article) {
    if (!article) return "";
    const raw = getLatestFullText(article);
    const cleaned = stripInvisible(raw).replace(/\s+/g, " ").trim();
    return cleaned;
}

function TickerChunk({ article, ariaHidden = false }) {
    const { p, dir, lang, t } = useLang();
    const text = tickerTextFor(article);
    if (!text) return null;
    const arOnly = lang !== "ar" && article.translation && !article.translation.available;
    return (
        <Link
            to={`${p}/article/${article.slug}`}
            dir={arOnly ? "rtl" : dir}
            className="ticker-item font-bold text-sm text-white hover:text-white/90"
            aria-hidden={ariaHidden || undefined}
            tabIndex={ariaHidden ? -1 : 0}
            data-testid={ariaHidden ? undefined : `ticker-article-${article.slug}`}
        >
            {arOnly && <span className="ticker-ar-tag" dir="ltr">[{t("arOnly")}] </span>}{text}
        </Link>
    );
}

function Separator({ ariaHidden = true }) {
    return (
        <span
            aria-hidden={ariaHidden}
            className="ticker-separator select-none"
        >
            ◆
        </span>
    );
}

export default function BreakingTicker({ articles = [] }) {
    const { t, lang } = useLang();
    const containerRef = useRef(null);
    const groupRef = useRef(null);
    const [groupW, setGroupW] = useState(0);
    const [containerW, setContainerW] = useState(0);
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== "undefined" && window.innerWidth <= 640,
    );
    // Bumped to force React to remount the animated node so the CSS
    // animation restarts from 0.
    const [restartKey, setRestartKey] = useState(0);

    // De-dupe by id and cap to three, preserving the incoming order.
    const trio = useMemo(() => {
        const seen = new Set();
        const out = [];
        // EN: translated stories first so the rail reads in English; Arabic-only ones only fill the gaps.
        const ordered = lang !== "ar" ? [...articles].sort((a, b) => Number(!!b?.translation?.available) - Number(!!a?.translation?.available)) : articles;
        for (const a of ordered) {
            if (!a?.id || seen.has(a.id)) continue;
            seen.add(a.id);
            out.push(a);
            if (out.length >= 3) break;
        }
        return out;
    }, [articles, lang]);

    // Signature used to detect real content changes vs render churn.
    const signature = useMemo(
        () => trio.map((a) => `${a.id}|${a.title}|${a.updated_at || a.published_at || ""}`).join("~"),
        [trio],
    );

    useEffect(() => {
        setRestartKey((k) => k + 1);
    }, [signature]);

    useEffect(() => {
        if (!trio.length) return;
        const measure = () => {
            if (containerRef.current) {
                const w = containerRef.current.clientWidth;
                if (w > 0) setContainerW(w);
            }
            if (groupRef.current) {
                const w = groupRef.current.getBoundingClientRect().width;
                if (w > 0) setGroupW(Math.ceil(w));
            }
            setIsMobile(window.innerWidth <= 640);
        };
        measure();
        if (typeof document !== "undefined" && document.fonts?.ready) {
            document.fonts.ready.then(measure).catch(() => {});
        }
        const raf = requestAnimationFrame(() => {
            measure();
            requestAnimationFrame(measure);
        });
        const ro = new ResizeObserver(measure);
        if (containerRef.current) ro.observe(containerRef.current);
        if (groupRef.current) ro.observe(groupRef.current);
        window.addEventListener("resize", measure);
        window.addEventListener("orientationchange", measure);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            window.removeEventListener("resize", measure);
            window.removeEventListener("orientationchange", measure);
        };
    }, [signature, trio.length]);

    // Restart animation when the tab comes back to the foreground (Safari
    // sometimes leaves it paused after a bfcache round-trip).
    useEffect(() => {
        const kick = () => setRestartKey((k) => k + 1);
        const onVis = () => { if (!document.hidden) kick(); };
        window.addEventListener("pageshow", kick);
        document.addEventListener("visibilitychange", onVis);
        return () => {
            window.removeEventListener("pageshow", kick);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, []);

    if (!trio.length) return null;

    const speed = isMobile ? SPEED_MOBILE : SPEED_DESKTOP;
    const effectiveGroupW = groupW > 0 ? groupW : FALLBACK_GROUP_WIDTH;
    const durationSec = Math.max(8, effectiveGroupW / speed);
    // Each group is forced to at least the container width. That makes sure
    // one group always covers the whole viewport, so between the two groups
    // the rail is never uncovered — no empty red space appears at any point
    // of the animation, even for very short headlines. The measured
    // `containerWidth` is 0 on the first render; we fall back to a safe
    // value so the animation still starts.
    const groupMinWidth = containerW > 0 ? containerW : 320;

    return (
        <div className="bg-brand-red text-white overflow-hidden w-full" data-testid="breaking-ticker">
            <div className="mx-auto max-w-7xl flex items-stretch">
                <span className="beam-ring beam-ring--slow beam-ring--badge shrink-0 my-1.5 ms-1 rounded-[5px]">
                    <div
                        className="px-3 h-full bg-brand-black font-bold text-sm font-display whitespace-nowrap flex items-center gap-2 shrink-0 select-none"
                        data-testid="breaking-ticker-label"
                        aria-hidden="false"
                        role="heading"
                        aria-level="2"
                    >
                        <LiveDot tone="red" testId="breaking-live-dot" />
                        {t("breaking")}
                    </div>
                </span>
                <div
                    ref={containerRef}
                    dir="ltr"
                    className="flex-1 min-w-0 py-2 overflow-hidden relative"
                    data-testid="breaking-ticker-rail"
                    style={{ whiteSpace: "nowrap" }}
                >
                    <div
                        key={restartKey}
                        className="ticker-track"
                        style={{ "--ticker-duration": `${durationSec}s` }}
                    >
                        {/* First group — visible and readable. */}
                        <div
                            ref={groupRef}
                            className="ticker-group"
                            style={{ minWidth: `${groupMinWidth}px` }}
                        >
                            {trio.map((a) => (
                                <span key={a.id} className="ticker-cell">
                                    <TickerChunk article={a} />
                                    <Separator />
                                </span>
                            ))}
                        </div>
                        {/* Second group — pure decoration for the seamless
                            loop; hidden from assistive tech. */}
                        <div
                            className="ticker-group"
                            aria-hidden="true"
                            style={{ minWidth: `${groupMinWidth}px` }}
                        >
                            {trio.map((a) => (
                                <span key={`copy-${a.id}`} className="ticker-cell">
                                    <TickerChunk article={a} ariaHidden />
                                    <Separator />
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
